import React, { useState, useEffect, useMemo, useRef } from 'react';
import { API } from '../utils/helpers';

// ── Day, time and fitter for a fitting (3 Oct) ────────────────────────────────
//
// Used by the booking page (a new fit) and the manage page (moving one). Reads
// /book/fit/slots: every start time on the days that can be booked, with who
// is free for it. A time nobody is free for is shown struck through rather
// than left out, so the day does not look like it has gaps for no reason.
//
// query: what identifies the request, either { shop, itemID } or { token }.
// C: the page's colours. value: { startsAt, fitterUserId, date } or null.
// onMeta: called once with { questions, deposit, minutes, freeCancelHours }.

// Minutes after midnight -> '2:00 PM'. Shop time, as the server's labels are,
// whatever zone the customer's phone is in.
const clock = (min) => {
  const h = Math.floor(min / 60) % 24, m = min % 60;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const fmtDay = (date, opts) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', opts);
};

export default function FitTimePicker({ query, C, value, onChange, onMeta }) {
  const [fitter, setFitter] = useState(value?.fitterUserId || 'any');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [day, setDay] = useState(value?.date || null);
  const metaSent = useRef(false);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) p.set(k, v);
    if (fitter && fitter !== 'any') p.set('fitter', fitter);
    return p.toString();
  }, [query, fitter]);

  useEffect(() => {
    let live = true;
    setError('');
    fetch(`${API}/book/fit/slots?${qs}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Could not load times.'); return d; })
      .then(d => {
        if (!live) return;
        setData(d);
        if (!metaSent.current && onMeta) { metaSent.current = true; onMeta({ questions: d.questions, deposit: d.deposit, minutes: d.minutes, freeCancelHours: d.freeCancelHours, fitters: d.fitters }); }
        const open = Object.keys(d.days).filter(k => d.days[k].some(s => s.free.length)).sort();
        setDay(prev => (prev && open.includes(prev) ? prev : open[0] || null));
      })
      .catch(err => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [qs]);

  if (error) return <div style={{ fontSize: 13, color: C.danger, margin: '4px 0 10px' }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 13, color: C.sub, margin: '4px 0 10px' }}>Loading times…</div>;

  const dates = Object.keys(data.days).sort();
  const hasFree = (k) => data.days[k].some(s => s.free.length);
  const slots = day ? data.days[day] || [] : [];
  const nameOf = (id) => (data.fitters.find(f => String(f.userId) === String(id)) || {}).name || '';
  const chosen = value && slots.find(s => s.startsAt === value.startsAt);
  const card = (sel) => ({
    border: sel ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: 10, padding: sel ? '9px 11px' : '10px 12px',
    background: sel ? C.accentTint : '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', minWidth: 0,
  });

  const pickFitter = (id) => { setFitter(id); onChange(null); };

  return (
    <div>
      {data.fitters.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, data.fitters.length + 1)}, minmax(0, 1fr))`, gap: 8, marginBottom: 12 }}>
          {[{ userId: 'any', name: 'Any fitter', workDays: 'Most times available' }, ...data.fitters].map(f => {
            const sel = String(fitter) === String(f.userId);
            return (
              <button key={f.userId} type="button" onClick={() => pickFitter(f.userId)} style={card(sel)} aria-pressed={sel}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{f.workDays}</div>
              </button>
            );
          })}
        </div>
      )}

      {dates.length === 0 ? (
        <div style={{ fontSize: 13, color: C.sub, margin: '4px 0 10px' }}>No times are open for booking right now. Please call the shop.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 8, WebkitOverflowScrolling: 'touch' }}>
            {dates.map(k => {
              const sel = k === day, open = hasFree(k);
              return (
                <button key={k} type="button" disabled={!open} onClick={() => { setDay(k); onChange(null); }}
                  style={{ flex: '0 0 auto', width: 58, padding: '7px 0', borderRadius: 9, fontFamily: 'inherit', textAlign: 'center',
                    border: sel ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: sel ? C.accent : '#fff',
                    color: sel ? '#fff' : open ? C.text : C.disabled, cursor: open ? 'pointer' : 'default' }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{fmtDay(k, { weekday: 'short' })}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, textDecoration: open ? 'none' : 'line-through' }}>{fmtDay(k, { month: 'short', day: 'numeric' })}</div>
                </button>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
            {slots.map(s => {
              const sel = value && value.startsAt === s.startsAt;
              const free = s.free.length > 0;
              return (
                <button key={s.startsAt} type="button" disabled={!free}
                  onClick={() => onChange({ startsAt: s.startsAt, endsAt: s.endsAt, label: s.label, fitterUserId: fitter, date: day, free: s.free })}
                  style={{ padding: '10px 0', borderRadius: 9, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
                    border: sel ? `2px solid ${C.accent}` : `1px solid ${free ? C.border : C.borderSoft}`,
                    background: sel ? C.accent : '#fff', color: sel ? '#fff' : free ? C.text : C.disabled,
                    textDecoration: free ? 'none' : 'line-through', cursor: free ? 'pointer' : 'default' }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {chosen && (
        <div style={{ fontSize: 12.5, color: C.sub, margin: '10px 0 0', lineHeight: 1.5 }}>
          <strong style={{ color: C.text }}>{fmtDay(day, { weekday: 'long', month: 'long', day: 'numeric' })}</strong>
          {' · '}{chosen.label} to {clock(chosen.time + data.minutes)}
          {' · '}
          {fitter !== 'any' ? `with ${nameOf(fitter)}`
            : chosen.free.length === 1 ? `with ${nameOf(chosen.free[0])}` : 'with the first available fitter'}
        </div>
      )}
    </div>
  );
}
